import {
  attachNestedSprintsFromClipboard,
  cloneTaskFromClipboard,
  generateCustomFieldIds,
  isValidFokusClipboard,
  remapDependencies,
} from '../components/SprintDetail/FlowchartView/hooks/clipboardUtils';

function depIndex(dep) {
  if (typeof dep === 'number') return dep;
  if (dep && typeof dep.task === 'number') return dep.task;
  return null;
}

/**
 * Turn fokus v2 JSON into the payload the existing paste handler already takes.
 * IDs are regenerated here — same path as Ctrl+V.
 */
export function prepareClipboardPaste(clipboard) {
  if (!isValidFokusClipboard(clipboard)) return null;
  const timestamp = Date.now();
  const idMap = new Map();
  const optionIdMap = new Map();
  const {
    definitions,
    fieldIdMap,
    optionIdMap: customFieldOptionIdMap,
  } = generateCustomFieldIds(clipboard.customFieldDefinitions || [], timestamp);
  const tasks = clipboard.tasks.map((task, idx) =>
    cloneTaskFromClipboard(task, idMap, optionIdMap, timestamp, idx, fieldIdMap, customFieldOptionIdMap)
  );
  remapDependencies(tasks, idMap, optionIdMap);
  tasks.forEach((task) => {
    delete task.x;
    delete task.y;
    delete task._clipboardPos;
  });
  return {
    tasks,
    positions: {},
    // Instant tidy at +50ms runs before the new cards are measured, then
    // marks post-measure ELK as done — they stay stacked. Skip it and let
    // the canvas measure, then auto-layout.
    skipAutoLayout: true,
    nestedSprints: attachNestedSprintsFromClipboard(tasks, clipboard.nestedSprints, timestamp),
    tagDefinitions: clipboard.tagDefinitions || [],
    customFieldDefinitions: definitions,
  };
}

/**
 * Apply a modify preview onto existing missions (rename / rewire) and
 * clone any split additions. IDs for matched missions stay put.
 */
export function applyBrainModifyPreview(preview, existingTasks) {
  const nodes = Array.isArray(preview?.nodes) ? preview.nodes : [];
  if (!nodes.length) return null;
  const idMap = new Map();
  const timestamp = Date.now();
  const optionIdMap = new Map();
  for (const node of nodes) {
    if (node.missionId) idMap.set(node.index, node.missionId);
  }
  const created = [];
  for (const node of nodes) {
    if (node.missionId) continue;
    created.push(
      cloneTaskFromClipboard(
        { name: node.name, dependsOn: node.dependsOn || [] },
        idMap,
        optionIdMap,
        timestamp,
        node.index
      )
    );
  }
  remapDependencies(created, idMap, optionIdMap);
  created.forEach((task) => {
    delete task.x;
    delete task.y;
    delete task._clipboardPos;
    task.unlockConditions = [];
  });

  const next = (existingTasks || []).map((task) => {
    const node = nodes.find((item) => item.missionId === task.id);
    if (!node) return task;
    const depIds = (node.dependsOn || [])
      .map((dep) => idMap.get(depIndex(dep)))
      .filter(Boolean);
    return {
      ...task,
      name: node.name || task.name,
      dependsOn: depIds,
      unlockConditions: [],
      updatedAt: new Date().toISOString(),
    };
  });

  return {
    tasks: [...next, ...created],
    createdIds: created.map((task) => task.id),
  };
}
